import { Directive, nextTick } from "vue";
import Swiper, {
    Autoplay,
    EffectFade,
    Lazy,
    Navigation,
    Pagination,
    Thumbs,
} from "swiper";

Swiper.use([Navigation, Pagination, Thumbs, EffectFade, Lazy, Autoplay]);

const thumbs = (selector, orientationD, orientationM, spv1, spv2) => {
    return new Swiper(selector, {
        freeMode: true,
        watchSlidesVisibility: true,
        watchSlidesProgress: true,
        centerInsufficientSlides: true,
        slideToClickedSlide: true,
        navigation: {
            nextEl: ".thumbs__arrows-next",
            prevEl: ".thumbs__arrows-prev",
        },
        breakpoints: {
            320: {
                direction: orientationM,
                slidesPerView: spv2,
                spaceBetween: 13,
                loop: true,
            },
            768: {
                slidesPerView: spv1,
                spaceBetween: 10,
            },
            1025: {
                slidesPerView: spv1,
                direction: orientationD,
                spaceBetween: 10,
            },
        },
    });
};

const gallery = (
    selector: AppGalleryHTMLElement,
    btnNext: HTMLElement,
    btnPrev: HTMLElement,
    pagSelector?: any,
    thumbSelector?: any,
    autoPlay?: boolean,
) => {
    let playOptions = {};

    if (autoPlay) {
        playOptions = {
            loop: true,
            autoplay: {
                delay: 10000,
            },
        };
    }

    return new Swiper(selector, {
        ...playOptions,
        preloadImages: false,
        lazy: true,
        spaceBetween: 10,
        effect: "fade",
        fadeEffect: {
            crossFade: true,
        },
        navigation: {
            nextEl: btnNext,
            prevEl: btnPrev,
        },
        pagination: {
            el: pagSelector,
            type: "fraction",
        },
        thumbs: {
            swiper: thumbSelector,
        },
    });
};

type AppGalleryHTMLElement = HTMLElement & {
    _appGallerySwiper?: Swiper;
    _appGalleryThumbSwiper?: Swiper;
    _appGalleryTotalSlides?: number;
};

type AppGalleryOptions = {
    orientationD?: "horizontal" | "vertical";
    orientationM?: "horizontal" | "vertical";
    spv1?: number;
    spv2?: number;
    autoPlay?: boolean;
};

const createGallery = (
    el: AppGalleryHTMLElement,
    options: undefined | AppGalleryOptions,
) => {
    const nextBtn = el.querySelector(".swiper-button-next") as HTMLElement;
    const prevBtn = el.querySelector(".swiper-button-prev") as HTMLElement;

    if (options) {
        const pagination = el.querySelector(".swiper-pagination") as HTMLElement;
        const elThumb = (el.parentNode as HTMLElement).querySelector(
            ".slider__gallery-thumbs",
        ) as null | HTMLElement;
        const autoPlay = options.autoPlay;
        if (elThumb) {
            const orientationD = options.orientationD || "horizontal";
            const orientationM = options.orientationM || "horizontal";
            const spv1 = options.spv1 || 4.5;
            const spv2 = options.spv1 || 3;

            el._appGalleryThumbSwiper = thumbs(
                elThumb,
                orientationD,
                orientationM,
                spv1,
                spv2,
            );
        }
        el._appGallerySwiper = gallery(
            el,
            nextBtn,
            prevBtn,
            pagination,
            el._appGalleryThumbSwiper,
            autoPlay,
        );
    } else {
        el._appGallerySwiper = gallery(el, nextBtn, prevBtn, null, null, false);
    }

    el._appGalleryTotalSlides = el.querySelectorAll(
        ".swiper-slide:not(.swiper-slide-duplicate)",
    ).length;

    setTimeout(() => {
        el._appGallerySwiper?.update();
        el._appGalleryThumbSwiper?.update();
    }, 500);
};

export const appGalleryDirective: Directive<AppGalleryHTMLElement, AppGalleryOptions> = {
    mounted(el, bindings) {
        createGallery(el, bindings.value);
    },
    unmounted(el) {
        el._appGalleryThumbSwiper?.destroy(true, true);
        el._appGallerySwiper?.destroy(true, true);
    },
    updated(el, bindings) {
        const totalSlides = el.querySelectorAll(
            ".swiper-slide:not(.swiper-slide-duplicate)",
        ).length;

        if (totalSlides > (el._appGalleryTotalSlides || 0)) {
            el._appGallerySwiper?.destroy();
            el._appGalleryThumbSwiper?.destroy();
            el._appGalleryTotalSlides = totalSlides;

            nextTick(() => {
                createGallery(el, bindings.value);
            });
        } else {
            el._appGallerySwiper?.update();
            el._appGalleryThumbSwiper?.update();
        }
    },
};
